var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { isEmail } from 'validator';
import { Autocomplete, createFilterOptions } from '@emburse/ui-core';
import { UserAutoCompleteDisplay } from './UserAutoCompleteDisplay';
const PHYSICAL_CARD_HELPER_TEXT = 'Limit one physical card/user/card program. Invite new users using their email id.';
const VIRTUAL_CARD_HELPER_TEXT = 'Issue virtual cards for up to 10 people at a time.';
const ERROR_HELPER_TEXT = 'Select a name from the dropdown or enter a valid email address';
const filter = createFilterOptions();
/**
 * An Autocomplete for users that also supports adding new users by typing their email
 * Adapted from https://github.com/emburse/embark/blob/next/packages/embark-core/src/Autocomplete/AutocompleteDemo.tsx
 *
 * @param props.selectedId The ID of the selected user.  Should be falsy if there's no selection, or the selected user is a new one that doesn't exist in @param props.userOptions
 * @param props.selectedEmail The email of the selected user.  If the selected user is a new one that doesn't exist in @param props.userOptions, then @param props.selectedId will be falsy and this will be used to identify the user
 * @param props.userOptions The list of existing users
 * @param props.onChange The change handler.  Its argument is Partial<IUserExtension> because if a new email is entered, it will only have the displayName and email properties
 * @returns
 */
export const AddableUserAutocomplete = (_a) => {
    var { selectedId, selectedEmail, selectedIds, userOptions, disabled, multiple, onChange } = _a, rest = __rest(_a, ["selectedId", "selectedEmail", "selectedIds", "userOptions", "disabled", "multiple", "onChange"]);
    const defaultHelperText = multiple ? VIRTUAL_CARD_HELPER_TEXT : PHYSICAL_CARD_HELPER_TEXT;
    const [error, setError] = React.useState(false);
    const [helperText, setHelperText] = React.useState(defaultHelperText);
    const [options, setOptions] = React.useState(userOptions);
    const selectedValue = React.useMemo(() => {
        if (multiple) {
            if (selectedIds && (selectedIds === null || selectedIds === void 0 ? void 0 : selectedIds.length) > 0) {
                return options.filter((option) => selectedIds.find((x) => x === option.id || x === option.email));
            }
            return [];
        }
        if (selectedId) {
            return options.find((option) => option.id === selectedId);
        }
        else if (selectedEmail) {
            return options.find((option) => option.email === selectedEmail);
        }
        return null;
    }, [selectedId, selectedEmail, selectedIds, multiple, options]);
    const clearError = () => {
        setError(false);
        setHelperText(defaultHelperText);
    };
    const validate = (value) => {
        const isValidEmail = isEmail(value);
        if (isValidEmail) {
            clearError();
        }
        else {
            setHelperText(ERROR_HELPER_TEXT);
            setError(true);
        }
        const existingOption = options.find((option) => { var _a; return ((_a = option.email) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === (value === null || value === void 0 ? void 0 : value.toLowerCase()); });
        const formattedValue = existingOption || {
            displayName: value,
            email: value
        };
        onChange(formattedValue);
        if (isValidEmail && !existingOption) {
            setOptions([{ email: value, displayName: value }, ...options]);
        }
        return formattedValue;
    };
    const handleOnChangeCreate = (_event, newValue) => {
        clearError();
        if (disabled)
            return;
        if (multiple && Array.isArray(newValue)) {
            const filteredValue = [];
            newValue.forEach((val) => {
                if (val && val.inputValue) {
                    filteredValue.push(validate(val.inputValue));
                }
                else {
                    filteredValue.push(val);
                }
            });
            onChange(filteredValue);
            return;
        }
        if (typeof newValue === 'string') {
            onChange({
                displayName: newValue,
                email: newValue
            });
        }
        else if (newValue && newValue.inputValue) {
            validate(newValue.inputValue);
        }
        else {
            onChange(newValue);
        }
    };
    return (_jsx(Autocomplete, Object.assign({}, rest, { fullWidth: true, disabled: disabled, onChange: handleOnChangeCreate, error: error, helperText: helperText, options: options, autoHighlight: true, getOptionLabel: (option) => {
            return option.displayName;
        }, multiple: multiple ? true : false, multipleType: "chips", value: selectedValue, renderOption: (props, option) => _jsx(UserAutoCompleteDisplay, { props: props, option: option }), filterOptions: (options, params) => {
            const filtered = filter(options, params);
            clearError();
            if (params.inputValue !== '') {
                filtered.push({
                    inputValue: params.inputValue,
                    displayName: `Add "${params.inputValue}"`
                });
            }
            return filtered;
        } })));
};
